import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import {
  BackendEliminarEvento,
  getCurrentDomain,
} from "../../../apis/backend/Api";
import mock_Json from "../../../dataJson/eliminarEvento.json";
import { formatearMensajeBackend } from "../../../apis/helper/helperApi";
import { TypeStatus } from "../../helpers/constantes";
import { IEliminarEvento } from "../../../interfaces/backendApi/eventoEliminar";

interface State {
  status: TypeStatus;
  isLoading: boolean;
  error: string | null;
  Success: boolean;
  Mensaje: string;
}

const initialState: State = {
  status: "idle",
  isLoading: false,
  error: null,
  Success: false,
  Mensaje: "",
};

interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}

const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};

const eliminarEventoSlice = createSlice({
  name: "eliminarEvento",
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setClearEliminarEvento: (state) => {
      return initialState;
    },
  },
});

export const setBackendEliminarEvento =
  (datos: IEliminarEvento): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));

    try {
      const response: AxiosResponse = await BackendEliminarEvento(datos);

      const isError =
        response.data?.Success === false ||
        (response.data?.ResultadoSolicitud !== undefined &&
          response.data?.ResultadoSolicitud !== 200);

      if (isError) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));
        dispatch(setSuccess(false));
      } else {
        dispatch(setIsLoading(false));
        dispatch(setStatus("succeeded"));
        dispatch(setSuccess(true));
      }
      dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));

      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setSuccess(false));
      dispatch(setStatus("failed"));

      if (error.response?.status === 401) {
        dispatch(setStatus("noAutorice"));
        dispatch(
          setMensaje(
            formatearMensajeBackend(
              "Tu sesión ha expirado. Vuelve a iniciar sesión",
            ),
          ),
        );
      } else if (error.response) {
        // Hay respuesta del servidor, pero es otro error (400, 422, 500, etc.)
        console.error(
          "Error de servidor:",
          error.response.status,
          error.response.data,
        );

        const errorData = error.response.data;
        const mensajeBackend = errorData?.Mensaje;
        const resultadoSolicitud = errorData?.ResultadoSolicitud;

        if (
          mensajeBackend &&
          (resultadoSolicitud !== 200 || errorData?.Success === false)
        ) {
          dispatch(setMensaje(formatearMensajeBackend(mensajeBackend)));
        } else {
          dispatch(
            setMensaje(
              `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
            ),
          );
        }
      } else if (error.request) {
        console.error("Error de conexión:", error.message);
        dispatch(
          setMensaje(
            "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
          ),
        );
      } else {
        console.error("Error de red o solicitud:", error.message);
        dispatch(
          setMensaje(
            "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
          ),
        );
      }
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
      if (getCurrentDomain() === "localhost") {
        dispatch(setIsLoading(true));
        let response: AxiosResponse = defaultResp;
        response.data = mock_Json;

        const mockData: any = mock_Json;
        const isErrorMock =
          mockData?.Success === false ||
          (mockData?.ResultadoSolicitud !== undefined &&
            mockData?.ResultadoSolicitud !== 200);

        if (isErrorMock) {
          dispatch(setIsLoading(false));
          dispatch(setStatus("failed"));
          dispatch(setSuccess(false));
        } else {
          dispatch(setIsLoading(false));
          dispatch(setStatus("succeeded"));
          dispatch(setSuccess(true));
        }
        dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
        const mock401 = await import("../../../dataJson/401.json");
        if (mock401.status === 401) {
          dispatch(setStatus("noAutorice"));
          dispatch(
            setMensaje(
              formatearMensajeBackend(
                "Tu sesión ha expirado. Vuelve a iniciar sesión",
              ),
            ),
          );
        }
      }
    }
  };

export const setBackendClearEliminarEvento =
  (): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));
    try {
      let response: AxiosResponse = defaultResp;
      dispatch(setClearEliminarEvento());
      dispatch(setIsLoading(false));
      dispatch(setStatus("idle"));
      dispatch(setSuccess(false));
      dispatch(setMensaje(""));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setStatus("failed"));
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const {
  setIsLoading,
  setStatus,
  setSuccess,
  setMensaje,
  setError,
  setClearEliminarEvento,
} = eliminarEventoSlice.actions;

export default eliminarEventoSlice.reducer;
